import React, { useEffect, useState } from "react";

import { useTheme } from "../../context/ThemeContext";
import ArrowRightBigIcon from "../../images/icons/arrow-right-big-icon.inline.svg";
import ArrowRightIcon from "../../images/icons/arrow-right-icon.inline.svg";
import { stripHtmlTags } from "../../utils/commonFunctions";
import { Link } from "../../utils/customLink";
import Image from "../image";

const cardDescriptionLength = 80;

const extractDescriptionText = (content) => {
  if (!content) return "";

  const paragraphElements = content
    .replaceAll("<p>&nbsp;</p>", "")
    .match(/<p[^>]*>(.*?)<\/p>/gi);
  if (paragraphElements && paragraphElements.length > 0) {
    const firstParagraphText =
      stripHtmlTags(paragraphElements[0])?.trim() || "";
    return firstParagraphText.length > cardDescriptionLength
      ? `${firstParagraphText.substring(0, cardDescriptionLength)}...`
      : firstParagraphText;
  }

  const strippedContent = stripHtmlTags(content)?.trim() || "";
  return strippedContent.length > cardDescriptionLength
    ? `${strippedContent.substring(0, cardDescriptionLength)}...`
    : strippedContent;
};

const ProgramCard = ({ program, descWidth, extLink }) => {
  const { colorMode, currentDomain, peopleDomain } = useTheme();
  const [programLink, setProgramLink] = useState("");

  useEffect(() => {
    const externalLinkTarget =
      process.env.GATSBY_PROJECT_NAV_TITLE.toLowerCase().includes("people")
        ? `https://www.${currentDomain}/`
        : peopleDomain;

    if (extLink) {
      const externalProgramLink = [
        externalLinkTarget,
        program.uri ?? program.link
      ].join("");
      setProgramLink(externalProgramLink);
    } else {
      setProgramLink(program.uri ?? program.link);
    }
  }, [currentDomain, peopleDomain, program.uri, program.link, extLink]);

  const type = program?.types?.map((type) => type);

  return (
    <div key={program.id} className="relative w-full mb-4 h-full sm:h-auto">
      <div className="gvi-program-card-box rounded-xs hover:rounded-none flex flex-col">
        <div className="relative">
          <Image
            imageClass="object-cover object-center w-full h-40 sm:h-72 rounded-md md:rounded-t-md md:rounded-b-none"
            sourceUrl={program?.sourceUrl}
            image={program?.image}
            imageAlt=""
          />
          <div className="overflow-hidden absolute z-50 left-4 bottom-0">
            <span
              className="bg-black-lighter planet:bg-gray-darker text-[12px] text-center text-white uppercase px-2 pt-2 pb-2"
              dangerouslySetInnerHTML={{
                __html: /journey/gi.test(type)
                  ? [type, " - NEW!"].join("")
                  : type
              }}
            />
          </div>
        </div>

        <div className="flex flex-col items-center h-full sm:h-20 gap-4 md:px-6 pt-4 sm:py-4 bg-transparent md:bg-white planet:md:gvi-dark-gray-bg rounded-b md:justify-between md:flex-row">
          <div className={`md:${descWidth} w-full h-5 sm:h-auto`}>
            <span
              className="text-mob md:text-base leading-genericCta font-bold md:font-normal text-left text-generic-text planet:text-white"
              dangerouslySetInnerHTML={{
                __html: program?.title
              }}
            />
            {/journey/gi.test(type) && program?.subtitle && (
              <div
                className={
                  "text-mob leading-[155.5%] text-black-lighter planet:gvi-dark-gray-text mt-14"
                }
                dangerouslySetInnerHTML={{ __html: program?.subtitle || "" }}
              />
            )}
          </div>
          <div className="py-6 sm:py-2 md:hidden flex-1 my-4">
            <div
              className="text-mob leading-[155.5%] text-black-lighter planet:text-gray-generic h-14 sm:h-auto"
              dangerouslySetInnerHTML={{
                __html: extractDescriptionText(program?.content) || ""
              }}
            />
          </div>
          <div className="md:block w-full md:w-2/5 min-w-[160px]">
            <Link
              to={programLink}
              className="view-program-link relative z-10 flex flex-row items-center justify-center 
              px-2 py-2 text-sm border text-generic-text planet:text-white border-generic-text 
              planet:border-white rounded-full planet:rounded-sm planet:hover:bg-white 
              hover:bg-black-lighter hover:text-white planet:hover:text-gray-darker"
            >
              <p className="text-base leading-genericCta font-bold pr-2">
                View program
              </p>
              {colorMode === "light" ? (
                <ArrowRightBigIcon fill="fill-black-lighter" />
              ) : (
                <ArrowRightIcon stroke="white" />
              )}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

ProgramCard.defaultProps = {
  descWidth: "w-2/3",
  extLink: false
};

export default ProgramCard;
